import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import TopNav from '../components/TopNav';
import { Envelope, Lock, Eye, EyeSlash, CheckCircle } from '@phosphor-icons/react';
import axios from 'axios';
import { usePlatformLogo } from '../hooks/usePlatformSettings';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPassword = () => {
  const [searchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get('token');
  const LOGO_URL = usePlatformLogo();

  const [step, setStep] = useState(tokenFromUrl ? 'reset' : 'request');
  const [email, setEmail] = useState('');
  const [resetToken, setResetToken] = useState(tokenFromUrl || '');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setSuccess(res.data.message);
      // In mock mode, auto-fill the token for testing
      if (res.data.mock_token) {
        setResetToken(res.data.mock_token);
        setStep('reset');
        setSuccess('Reset token generated (mock mode). Enter your new password below.');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/reset-password`, {
        token: resetToken,
        new_password: newPassword
      });
      setStep('done');
      setSuccess(res.data.message);
    } catch (err) {
      setError(err.response?.data?.detail || 'Reset failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50 flex flex-col items-center justify-center p-4">
      <div className="absolute top-4 left-4 z-20">
        <TopNav showBack showHome />
      </div>

      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/">
            <img src={LOGO_URL} alt="Look4Space" className="w-24 h-24 mx-auto mb-4 object-contain" />
          </Link>
          <h2 className="text-2xl font-bold text-slate-900 font-['Outfit']">
            {step === 'request' && 'Forgot Password'}
            {step === 'reset' && 'Reset Password'}
            {step === 'done' && 'Password Reset'}
          </h2>
          <p className="text-slate-500 mt-2">
            {step === 'request' && 'Enter your email to receive a reset link'}
            {step === 'reset' && 'Enter your new password'}
            {step === 'done' && ''}
          </p>
        </div>

        <div className="bg-white rounded-3xl shadow-xl shadow-blue-500/10 p-6 sm:p-8">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm" data-testid="error-message">{error}</div>
          )}
          {success && step !== 'done' && (
            <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-700 text-sm" data-testid="success-message">{success}</div>
          )}

          {/* Step 1: Request Reset */}
          {step === 'request' && (
            <form onSubmit={handleRequestReset} className="space-y-5">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Envelope className="text-slate-400" size={20} />
                </div>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address" className="pl-12 h-12 bg-slate-50 border-slate-200 focus:border-blue-500 focus:bg-white rounded-xl"
                  required data-testid="forgot-email-input" />
              </div>
              <Button type="submit" disabled={loading}
                className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl shadow-lg shadow-blue-500/20"
                data-testid="forgot-submit-btn">
                {loading ? 'Sending...' : 'Send Reset Link'}
              </Button>
            </form>
          )}

          {/* Step 2: Reset Password */}
          {step === 'reset' && (
            <form onSubmit={handleResetPassword} className="space-y-5">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Lock className="text-slate-400" size={20} />
                </div>
                <Input type={showPassword ? 'text' : 'password'} value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="New password" className="pl-12 pr-12 h-12 bg-slate-50 border-slate-200 focus:border-blue-500 focus:bg-white rounded-xl"
                  required data-testid="reset-password-input" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 pr-4 flex items-center">
                  {showPassword ? <EyeSlash className="text-slate-400" size={20} /> : <Eye className="text-slate-400" size={20} />}
                </button>
              </div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Lock className="text-slate-400" size={20} />
                </div>
                <Input type={showPassword ? 'text' : 'password'} value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password" className="pl-12 h-12 bg-slate-50 border-slate-200 focus:border-blue-500 focus:bg-white rounded-xl"
                  required data-testid="reset-confirm-password-input" />
              </div>
              <Button type="submit" disabled={loading}
                className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl shadow-lg shadow-blue-500/20"
                data-testid="reset-submit-btn">
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>
          )}

          {/* Step 3: Done */}
          {step === 'done' && (
            <div className="text-center py-4">
              <CheckCircle size={56} weight="duotone" className="text-emerald-500 mx-auto mb-4" />
              <p className="text-slate-700 font-medium mb-6">{success}</p>
              <Link to="/buyer/login">
                <Button className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl" data-testid="back-to-login-btn">
                  Back to Login
                </Button>
              </Link>
            </div>
          )}
        </div>

        <p className="mt-6 text-center text-slate-500">
          Remember your password?{' '}
          <Link to="/buyer/login" className="text-blue-600 hover:text-blue-700 font-semibold">Sign In</Link>
        </p>
      </div>
    </div>
  );
};

export default ForgotPassword;
